<template>
    <div v-if="loaded">
        <rkm-alert
            v-if="form.materiaisComMovimentacoesSemLivro.length > 0"
            color="primary"
        >
            <span>Existem movimentações de medicamentos sem livro disponível para registrar.</span>
            <rkm-button
                @click="$refs.ModalMaterialSemLivro.open()"
                class="ml-2"
                color="primary"
                small
            >
                Visualizar medicamentos
            </rkm-button>
        </rkm-alert>

        <rkm-form
            ref="Form"
            show-legend
        >
            <rkm-row>
                <rkm-col cols="2">
                    <rkm-form-select
                        :options="form.tiposPeriodo"
                        @change="handleTipoPeriodoChange"
                        label="Tipo de período"
                        v-model="model.tipoPeriodo"
                    />
                </rkm-col>

                <rkm-col cols="5">
                    <rkm-form-date-picker
                        :end-validations="validations.periodo"
                        :max-date="additionals.dataAtual"
                        :range-inline="false"
                        :start-validations="validations.periodo"
                        end-label="Até"
                        range
                        start-label="De"
                        v-model="model.periodo"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-select
                        :disabled="!hasPeriodo"
                        :options="form.livrosRegistro"
                        @change="handleLivroRegistroChange"
                        default-option-text="TODOS"
                        label="Livro"
                        show-default-option
                        text-field="nome"
                        v-model="model.livroRegistroId"
                        value-field="id"
                    />
                </rkm-col>

                <rkm-col cols="2">
                    <rkm-form-select
                        :disabled="!model.livroRegistroId"
                        :options="form.tiposLivroRegistro"
                        @change="handleLivroRegistroTipoChange"
                        default-option-text="TODOS"
                        label="Tipo"
                        show-default-option
                        text-field="nome"
                        v-model="model.livroRegistroTipoId"
                        value-field="id"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="2">
                    <rkm-form-select
                        :disabled="!hasPeriodo"
                        :options="form.status"
                        @change="handleStatusChange"
                        default-option-text="TODOS"
                        label="Status"
                        show-default-option
                        text-field="nome"
                        v-model="model.statusId"
                        value-field="id"
                    />
                </rkm-col>

                <rkm-col
                    v-if="isLivroPsicotropico"
                    cols="10"
                >
                    <rkm-form-input-autocomplete
                        :disabled="!hasPeriodo"
                        :repository="repository.material"
                        :request-query-string-params="materialRequestParams"
                        :search-fields="materialSearchFields"
                        label="Medicamento"
                        text-field="descricaoCompleta"
                        uppercase
                        v-model="model.material"
                    />
                </rkm-col>
            </rkm-row>
        </rkm-form>

        <rkm-data-table
            :fields="fields"
            :filter="localFilter"
            :repository="repository"
            :request-params="requestParams"
            :tbody-tr-class="rowClass"
            :reset-table="resetTable"
            :search-by="localSearchOption"
            :search-table="searchTable"
            class="table-livros"
            ref="DataTable"
            sort-by="abertoEm"
            sort-desc
        >

            <template v-slot:filters>
                <rkm-form-input
                    placeholder="Digite para buscar"
                    v-model="localFieldFilter"
                    @keyup.native.enter="handleEnterKey"
                >
                    <rkm-input-group-prepend slot="prepend">
                        <rkm-form-select
                            :options="searchOptions"
                            class="m-0"
                            v-model="localSearchOption"
                        />
                    </rkm-input-group-prepend>

                    <rkm-input-group-append slot="append">

                        <rkm-button
                            @click="$refs.Form.validate(search)"
                            color="primary"
                            class="btn-search"
                        >
                            Buscar
                        </rkm-button>
                        <rkm-button
                            :disabled="!localFieldFilter"
                            @click="clear"
                            class="btn-clear"
                        >
                            Limpar
                        </rkm-button>
                    </rkm-input-group-append>
                </rkm-form-input>
            </template>

            <template v-slot:cell(actions)="{ item }">
                <rkm-button
                    :authorize="`update-farmacia-${funcionalidade}`"
                    @click.stop="$router.push({ name: `farmacia-${funcionalidade}-edit`, params: { id: item.id } })"
                    color="primary"
                    small
                    title="Visualizar"
                >
                    <rkm-icon icon="eye" />
                </rkm-button>
            </template>

            <template #afterTable>
                <div class="my-2">
                    <p class="mb-0">
                        A linha em <span class="yellow">amarela</span> representa que o livro está com o
                        status aberto, porém já foi preenchido completamente.
                    </p>
                </div>
            </template>
        </rkm-data-table>

        <ModalMaterialSemLivro
            :materiais="form.materiaisComMovimentacoesSemLivro"
            ref="ModalMaterialSemLivro"
        />
    </div>
</template>

<script>
// Components.
import ModalMaterialSemLivro from './components/material-sem-livro/Modal'

// Others.
import { mapActions } from 'vuex'
import { repository, error, formats } from '@/mixins'
import livroStatus from '@/enums/farmacia/livroStatus'
import moment from 'moment'

export default {
    components: { ModalMaterialSemLivro },
    created() {

        this.repositories = {
            livroPsicotropico: this.getConectaSusRepository('farmacia.livroPsicotropico'),
            livroTalidomida: this.getConectaSusRepository('farmacia.livroTalidomida'),
        }

        this.load()
    },
    computed: {
        isLivroPsicotropico() {

            return this.funcionalidade === 'livro-psicotropico'
        },
        isLivroTalidomida() {

            return this.funcionalidade === 'livro-talidomida'
        },
        repository() {

            if (this.isLivroPsicotropico) {

                return this.repositories.livroPsicotropico
            }
            else if (this.isLivroTalidomida) {

                return this.repositories.livroTalidomida
            }

            return null
        },
        validations() {

            return {
                periodo: { required: true },
            }
        },
        hasPeriodo() {

            return this.model.periodo.start !== null && this.model.periodo.end !== null
        },
        materialSearchFields() {

            return [
                { key: 'material.codigo_material', searchable: true },
                { key: 'material.descricao', searchable: true },
            ]
        },
        materialRequestParams() {

            return {
                tipo_periodo: this.model.tipoPeriodo,
                periodo: {
                    start: this.model.periodo.start ? this.others.moment(this.model.periodo.start).format('DD/MM/YYYY') : null,
                    end: this.model.periodo.end ? this.others.moment(this.model.periodo.end).format('DD/MM/YYYY') : null,
                },
                livro_registro_id: this.model.livroRegistroId,
                livro_registro_tipo_id: this.model.livroRegistroTipoId,
                status_id: this.model.statusId,
            }
        },
        requestParams() {

            return {
                tipo_periodo: this.model.tipoPeriodo,
                periodo: {
                    start: this.model.periodo.start ? this.others.moment(this.model.periodo.start).format('DD/MM/YYYY') : null,
                    end: this.model.periodo.end ? this.others.moment(this.model.periodo.end).format('DD/MM/YYYY') : null,
                },
                livro_registro_id: this.model.livroRegistroId,
                livro_registro_tipo_id: this.model.livroRegistroTipoId,
                status_id: this.model.statusId,
                material_id: this.model.material ? this.model.material.id : null,
            }
        },
        fields() {

            return [
                {
                    name: 'livro.numero',
                    key: 'numero',
                    label: 'Número',
                    searchable: true,
                    sortable: true,
                    class: 'col-min text-center',
                },
                {
                    name: 'livro.abertoEm',
                    key: 'abertoEm',
                    label: 'Data de abertura',
                    searchable: false,
                    sortable: true,
                    class: 'col-min text-center',
                    formatter: data => this.formatDate(data)
                },
                {
                    name: 'livro.fechadoEm',
                    key: 'fechadoEm',
                    label: 'Data de encerramento',
                    searchable: false,
                    sortable: true,
                    class: 'col-min text-center',
                    formatter: data => data ? this.formatDate(data) : null
                },
                {
                    name: 'livroRegistro.nome',
                    key: 'livro',
                    label: 'Livro',
                    searchable: false,
                    sortable: true,
                },
                {
                    name: 'livroRegistroTipo.nome',
                    key: 'tipo',
                    label: 'Tipo',
                    searchable: false,
                    sortable: true,
                },
                {
                    name: 'status.nome',
                    key: 'status',
                    label: 'Status',
                    searchable: false,
                    sortable: true,
                    class: 'col-min text-center',
                },
                {
                    name: 'paginas',
                    key: 'paginas',
                    label: 'Páginas (Usadas/Total)',
                    searchable: false,
                    sortable: true,
                    class: 'col-min text-center',
                },
                {
                    key: 'actions',
                    label: 'Ações',
                    class: 'col-min',
                },
            ]
        },
        searchOptions() {
            return this.fields.filter(field => !!field.searchable).map(field => {
                return { text: field.label, value: field.key }
            })
        },
    },
    data: () => ({
        loaded: false,
        additionals: null,
        form: null,
        model: null,
        enums: {
            livroStatus,
        },
        localFieldFilter: null,
        localFilter: null,
        localSearchOption: null,
        searchTable: false,
        others: {
            moment
        },
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load(afterUpdate = false) {

            this.setLoading()

            this.repository.list()
                .then(({ data }) => {

                    if (afterUpdate) {

                        this.form.materiaisComMovimentacoesSemLivro = data.form.materiaisComMovimentacoesSemLivro

                        return
                    }

                    this.additionals = data.additionals
                    this.form = data.form
                    this.model = data.model

                    this.addWatchers()

                    this.loaded = true
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        addWatchers() {

            this.$watch('model.periodo', {
                deep: true,
                handler: this.handlePeriodoChange
            })
        },
        rowClass(item, type) {

            if (type === 'row' && item.statusId == this.enums.livroStatus.ABERTO) {

                if (item.totalmentePreenchido) {

                    return 'table-warning'
                }
                else if (item.hasAvailableUpdate) {

                    return 'table-primary'
                }
            }
        },
        handleTipoPeriodoChange() {

            this.form.livrosRegistro = []
            this.form.tiposLivroRegistro = []
            this.form.status = []

            this.model.periodo.start = null
            this.model.periodo.end = null
            this.model.livroRegistroId = null
            this.model.livroRegistroTipoId = null
            this.model.statusId = null
            this.model.material = null

            this.clearTable()
        },
        handlePeriodoChange() {

            this.form.livrosRegistro = []
            this.form.tiposLivroRegistro = []
            this.form.status = []

            this.model.livroRegistroId = null
            this.model.livroRegistroTipoId = null
            this.model.statusId = null
            this.model.material = null

            this.clearTable()

            if (this.model.periodo.start === null || this.model.periodo.end === null) return

            const params = {
                load_from: 'livros-registro',
                tipo_periodo: this.model.tipoPeriodo,
                periodo: {
                    start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                },
            }

            this.setLoading()

            this.repository.filter(params)
                .then(({ data }) => {
                    this.form.livrosRegistro = data.livrosRegistro
                    this.form.tiposLivroRegistro = data.tiposLivroRegistro
                    this.form.status = data.status
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleLivroRegistroChange() {

            this.form.tiposLivroRegistro = []
            this.form.status = []

            this.model.livroRegistroTipoId = null
            this.model.statusId = null
            this.model.material = null

            this.clearTable()

            const params = {
                load_from: 'tipos-livro-registro',
                tipo_periodo: this.model.tipoPeriodo,
                periodo: {
                    start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                },
                livro_registro_id: this.model.livroRegistroId,
            }

            this.setLoading()

            this.repository.filter(params)
                .then(({ data }) => {
                    this.form.tiposLivroRegistro = data.tiposLivroRegistro
                    this.form.status = data.status
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleLivroRegistroTipoChange() {

            this.form.status = []

            this.model.statusId = null
            this.model.material = null

            this.clearTable()

            const params = {
                load_from: 'status',
                tipo_periodo: this.model.tipoPeriodo,
                periodo: {
                    start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                },
                livro_registro_id: this.model.livroRegistroId,
                livro_registro_tipo_id: this.model.livroRegistroTipoId,
            }

            this.setLoading()

            this.repository.filter(params)
                .then(({ data }) => {
                    this.form.status = data.status
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleStatusChange() {

            this.model.material = null

            this.clearTable()
        },
        search() {

            this.searchTable = true
            this.resetTable = false
            this.localFilter = this.localFieldFilter
            this.$refs.DataTable.refresh();
        },
        clear() {

            if (this.model) {
                this.model.livroRegistroId = null
                this.model.livroRegistroTipoId = null
                this.model.statusId = null
            }

            this.localSearchOption = this.searchBy || this.searchOptions[0].value;
            this.clearSearchOption()
        },
        clearSearchOption() {
            this.localFieldFilter = null
            this.showNaoRegularizados = false
            this.clearTable()
        },
        clearTable() {
            this.localFilter = null
            this.resetTable = true
            this.searchTable = false
        },
        handleEnterKey(event) {

            if (event.key === 'Enter') {
                this.search();
            }
        },
    },
    mounted() {
        if (this.searchOptions.length > 0) {
            this.localSearchOption = this.searchOptions[0].value;
        }
    },
    mixins: [repository, error, formats],
    props: {
        funcionalidade: { type: String, required: true },
    },
    watch: {
        'localSearchOption': function () {

            this.clearSearchOption()
        },
        'localFieldFilter': function () {

            this.clearTable()
        },
    },
}
</script>

<style scoped>
.table-livros >>> .yellow { color: #e6c619; }
.btn-search {
    border-top-right-radius: 0.25rem!important;
    border-bottom-right-radius: 0.25rem!important;
}
.btn-clear {
    border-top-left-radius: 0.25rem!important;
    border-bottom-left-radius: 0.25rem!important;
}
</style>